<template>
    <DashboardContentLayout class="space-y-6" :heading="$t('views.objectives.header.heading')" :has-pig-type-picker="true" :slim-pig-type-picker="true">
        <div class="border-t border-t-gray-200 mt-5 mb-6" aria-hidden="true" />

        <!-- Reference-window toggle: switches the whole table between the 1/4/8 (Weaner) or
             1/4/13 (SlaughterPig) week views at once. Right-aligned, same pill-group visual as Section
             Status's mortality-view toggle. Manage targets sits alongside it - it's the one control
             that edits the targets this whole toggle measures against.

             Label sits above the pill-group rather than beside it, since "weeks" alone on the pills
             reads ambiguous without it and placing it inline would crowd the row - `items-end` on
             this row, not `items-center`, so the label's own line doesn't push the pill-group's
             vertical centre down away from Manage targets/Top performers next to it; the pill-group is
             the last (only) line in its column, so its bottom edge lines up with the buttons' own
             regardless of the label's height. -->
        <div class="flex flex-wrap items-end justify-end gap-3">
            <div class="flex flex-col gap-1">
                <span class="text-xs font-medium text-gray-400">{{ $t('views.objectives.referenceWindow.label') }}</span>
                <div class="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
                    <button
                        v-for="option in referenceWindowOptions"
                        :key="option"
                        type="button"
                        class="rounded-md px-3 py-1.5 text-sm font-medium transition-colors"
                        :class="referenceWindowWeeks === option ? 'bg-primary-700 text-white' : 'text-gray-600 hover:bg-gray-100'"
                        @click="selectReferenceWindow(option)"
                    >
                        {{ $t('views.objectives.referenceWindow.weeksOption', { count: option }) }}
                    </button>
                </div>
            </div>
            <ManageObjectivesModal ref="targetsModal" :is-weaner="isWeaners" @saved="refetchAll" />
            <!-- Top performers (gamification) - next to Targets since both are toolbar-level,
                 Directory-wide controls, not something scoped to one Farm's tile. Always rendered;
                 disabled (not v-if'd away) until there's something worth showing it for (>= 3 Farms,
                 at least one qualifying Barn) - see leaderboard.ts and leaderboardDisabledReason. -->
            <TopPerformersModal :best="leaderboard.best" :worst="leaderboard.worst" :disabled-reason="leaderboardDisabledReason" :window-weeks="referenceWindowWeeks" />
        </div>

        <FadeTransition mode="out-in">
            <div v-if="isLoading" key="loading" class="flex items-center justify-center px-4 py-16">
                <Spinner class="h-10 w-10" />
            </div>
            <div v-else-if="isError" key="error" class="flex flex-col items-center gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-6 text-center text-red-700">
                <p class="text-base font-medium">{{ $t('views.objectives.texts.loadError') }}</p>
                <Button variant="secondary" size="sm" @click="refetch">{{ $t('views.objectives.texts.retry') }}</Button>
            </div>
            <!-- Nothing in the current navigator selection. -->
            <div v-else-if="!hasFarms" key="empty" class="flex items-center justify-center px-4 py-16">
                <div class="text-center text-gray-500">{{ $t('views.objectives.texts.noFarms') }}</div>
            </div>
            <!-- Farm groups render as one real <table> for the whole page, not per-row divs. Farm is a
                 colspan(5) label row (plain small/gray type, no rule, no badge); Barn/Section rows are
                 genuine sibling <tr>s in this SAME table, so they share the header's own column grid -
                 real browser-computed alignment, no hand-synced Tailwind width classes duplicated
                 across BarnRow/SectionRow/this file. Only the header's own bottom border shows; no
                 per-row or per-cell borders anywhere else (the deviation verdict's coloured
                 left-accent still renders, just as a border-left on the Name cell specifically, not
                 the whole row - same visual result, since every cell in a <tr> shares that row's
                 height). Farm→Barn nesting is plain left-padding on the Name cell (no line);
                 Barn→Section keeps its vertical tree guide-line, drawn inside the Name cell (see
                 SectionRow.vue).

                 Columns: Name | Mortality (bar+value) | Trend | Pigs to Target | Dead - Trend and
                 Pigs to Target are each their own column.

                 Every column has an explicit fixed width via <colgroup> (table-layout: fixed) so every
                 row's columns line up consistently regardless of content length - `w-full` on top of
                 that scales the five widths proportionally to fill the container, rather than
                 following their raw rem widths on a wide screen.

                 Wrapped in a bordered/backgrounded tile. This is the only "card" left on the page on
                 purpose - it wraps the whole table once, not per-Farm/per-Barn - one outer tile reads
                 as "here's the list," not boxes-in-a-box. -->
            <!-- Dimmed + inert rather than swapped for the Spinner branch above while
                 `isRefetchingPlaceholder` is true: the previous animal type's rows stay visible as a
                 reference point during a placeholder-backed refetch, instead of flashing to a blank
                 Spinner on every toggle click. -->
            <div
                v-else
                key="content"
                class="mt-2 rounded-xl border border-gray-200 bg-white p-4 transition-opacity"
                :class="{ 'pointer-events-none opacity-50': isRefetchingPlaceholder }"
                :aria-busy="isRefetchingPlaceholder"
            >
                <table class="w-full text-sm" style="table-layout: fixed; border-collapse: collapse">
                    <colgroup>
                        <col style="width: 15rem" />
                        <col style="width: 9rem" />
                        <col style="width: 3.5rem" />
                        <col style="width: 5rem" />
                        <col style="width: 4.5rem" />
                    </colgroup>
                    <thead>
                        <tr class="border-b border-gray-200 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                            <th class="py-2 pl-2 pr-2"></th>
                            <th class="py-2 pr-2">{{ $t('views.objectives.texts.mortalityColumnHeader') }}</th>
                            <th class="py-2 pr-4">{{ $t('views.objectives.texts.trendColumnHeader') }}</th>
                            <th class="py-2 pr-2">{{ $t('views.objectives.texts.pigsToTargetColumnHeader') }}</th>
                            <th class="py-2 pr-1 text-right">{{ $t('views.objectives.texts.deadColumnHeader') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="farm in farmGroups" :key="farm.id">
                            <tr>
                                <td colspan="5" class="pl-2 pr-2 pt-3 pb-1">
                                    <div class="flex flex-wrap items-center gap-2">
                                        <span class="text-[12px] font-semibold text-gray-500">{{ farm.name }}</span>
                                        <span v-if="resolveFarmUniformTarget(farm) != null" class="text-[11px] font-medium text-gray-400">
                                            · {{ $t('views.objectives.texts.targetCaption', { value: $n(resolveFarmUniformTarget(farm) as number, 'decimal') }) }}
                                        </span>
                                        <!-- On-demand trend toggle - absent entirely when there's no history
                                             for this Farm at all, so there's nothing to click when there's nothing to show. -->
                                        <button
                                            v-if="historyByFarmId.get(farm.id)"
                                            type="button"
                                            class="ml-auto grid h-6 w-6 shrink-0 place-content-center rounded-md transition-colors hover:bg-gray-100"
                                            :class="isChartExpanded(farm.id) ? 'bg-primary-50 text-primary-700' : 'text-gray-400 hover:text-gray-600'"
                                            :title="isChartExpanded(farm.id) ? $t('views.objectives.texts.hideTrend') : $t('views.objectives.texts.showTrend')"
                                            :aria-expanded="isChartExpanded(farm.id)"
                                            :aria-label="`${isChartExpanded(farm.id) ? $t('views.objectives.texts.hideTrend') : $t('views.objectives.texts.showTrend')}: ${farm.name}`"
                                            @click="toggleChart(farm.id)"
                                        >
                                            <LineChart class="h-4 w-4" aria-hidden="true" />
                                        </button>
                                    </div>
                                </td>
                            </tr>

                            <BarnRow
                                v-for="barn in farm.barns"
                                :key="barn.id"
                                :barn="barn"
                                :expanded="isExpanded(barn.id)"
                                :ceiling="barCeiling"
                                :farm-location-id="farm.id"
                                :trend-direction="trendDirectionFor(farm.id, barn.id)"
                                @toggle="toggle(barn.id)"
                                @set-target="openTargetsFor"
                            />

                            <tr v-if="isChartExpanded(farm.id) && historyByFarmId.get(farm.id)">
                                <td colspan="5" class="py-2 pl-2 pr-2">
                                    <ObjectiveTrendSparkline
                                        :farm-name="farm.name"
                                        :barns="historyByFarmId.get(farm.id)!.barns"
                                        :resolved-target="historyByFarmId.get(farm.id)!.resolvedTarget"
                                        :target-source="historyByFarmId.get(farm.id)!.targetSource"
                                    />
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </FadeTransition>
    </DashboardContentLayout>
</template>

<script lang="ts" setup>
import { LineChart } from 'lucide-vue-next';
import DashboardContentLayout from '@/components/layouts/dashboardLayout/content/DashboardContentLayout.vue';
import FadeTransition from '@/components/transitions/FadeTransition.vue';
import Spinner from '@/components/ui/Spinner.vue';
import { Button } from '@/components/ui/button';
import ManageObjectivesModal from '@/components/common/objectives/ManageObjectivesModal.vue';
import TopPerformersModal from './TopPerformersModal.vue';
import BarnRow from './BarnRow.vue';
import ObjectiveTrendSparkline from './ObjectiveTrendSparkline.vue';
import { resolveFarmUniformTarget } from './objectiveRow';
import { useObjectivesPage } from './useObjectivesPage';

const {
    isWeaners,
    referenceWindowOptions,
    referenceWindowWeeks,
    selectReferenceWindow,
    isLoading,
    isError,
    refetch,
    isRefetchingPlaceholder,
    farmGroups,
    hasFarms,
    refetchAll,
    historyByFarmId,
    trendDirectionFor,
    leaderboard,
    leaderboardDisabledReason,
    barCeiling,
    isExpanded,
    toggle,
    isChartExpanded,
    toggleChart,
    targetsModal,
    openTargetsFor,
} = useObjectivesPage();
</script>
